import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Building2, ShieldCheck, FileText, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-track-6/artifacts/ylx5ssnx_Insite%20Logo%204.png';
const REMEMBER_EMAIL_KEY = 'insite360_remembered_email';

// NYC Skyline image from Unsplash (free to use)
const NYC_IMAGE_URL = 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=2070&auto=format&fit=crop';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      
      // Handle remember me - save or remove email
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, formData.email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
      
      toast.success('Welcome back!');
      navigate('/');  // Go to city selector first
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Invalid credentials';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 py-12 lg:px-16 relative">
        {/* Background subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-zinc-950 z-0" />
        
        <div className="relative z-10 max-w-md mx-auto w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center mb-10" data-testid="login-logo">
            <img src={LOGO_URL} alt="Pivotal Insite360" className="h-12 w-auto" />
          </Link>

          {/* Welcome Text */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-zinc-400">Sign in to access your compliance dashboard</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300 text-sm">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="input-dark w-full h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300 text-sm">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="input-dark w-full pr-10 h-11"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember-me" 
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked)}
                  className="border-zinc-600 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                  data-testid="remember-me-checkbox"
                />
                <Label 
                  htmlFor="remember-me" 
                  className="text-sm text-zinc-400 cursor-pointer select-none"
                >
                  Remember me
                </Label>
              </div>
              <Link 
                to="/forgot-password" 
                className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full h-11 text-base"
              data-testid="login-submit-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              Sign In
            </Button>
          </form>

          <p className="text-center text-zinc-400 mt-8">
            Don't have an account?{' '}
            <Link to="/register" className="text-indigo-400 hover:text-indigo-300 font-medium" data-testid="register-link">
              Get started
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - NYC Image with Overlay */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${NYC_IMAGE_URL})` }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-transparent to-zinc-950/40" />
        
        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          {/* Stats Banner */}
          <div className="glass-card rounded-xl p-6 backdrop-blur-md bg-zinc-900/60 border border-white/10 mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Monitor Building Compliance in Real-Time
            </h2>
            <p className="text-zinc-300 mb-6">
              Access violations, permits, and compliance records across NYC and Washington DC
            </p>
            
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">41M+</div>
                <div className="text-xs text-zinc-400">NYC Records</div>
              </div>
              <div className="text-center border-x border-white/10">
                <div className="text-3xl font-bold text-white">180K+</div>
                <div className="text-xs text-zinc-400">DC Records</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">17</div>
                <div className="text-xs text-zinc-400">Data Sources</div>
              </div>
            </div>
          </div>
          
          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <Building2 className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-white">DOB Violations</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-white">Fire & ECB</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <FileText className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-white">Permits</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <Bell className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-white">Real-time Alerts</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
